import { useState } from 'react';
import { billetPaid, invoice, pixSent, userGroup, vacation } from '@ibk/shared-assets';
import { useTranslation } from '@ibk/shared-translation';
import { formatCurrency, formatLongDayAndMonthOrToday } from '@ibk/shared-utils';
import { BillType, OperationCode } from '../../enums';
export function useFormatAccountsPayable() {
    const { t } = useTranslation();
    const [totalAmountAccountsPayable, setTotalAmountAccountsPayable] = useState(0);
    function getDateIndex(date, accountsPayable) {
        const dateIndex = accountsPayable.findIndex((value) => value.date === date);
        if (dateIndex >= 0) {
            return dateIndex;
        }
        return accountsPayable.push({ date, data: [] }) - 1;
    }
    function getData(accountPayable, isWhiteListed) {
        const isGroupedOperation = accountPayable.operationCode === OperationCode.PAYROLL ||
            accountPayable.operationCode === OperationCode.EMPLOYEE_13TH_SALARY;
        const isGroupedNameAndIcon = accountPayable.groupingDetails && isGroupedOperation && isWhiteListed;
        const isBillTypeDependent = accountPayable.operationCode === OperationCode.TR_FEES ||
            accountPayable.operationCode === OperationCode.TAX ||
            accountPayable.operationCode === OperationCode.UTILITIES ||
            accountPayable.operationCode === OperationCode.INVOICE;
        if (isGroupedNameAndIcon) {
            return {
                image: pixSent,
                title: accountPayable.operationCode === OperationCode.PAYROLL
                    ? t('accounts-payable.operation-type-payroll')
                    : t('accounts-payable.operation-type-13th-salary'),
                description: t('accounts-payable.count-employee-payments', {
                    count: accountPayable.groupingDetails?.count ?? 0,
                }),
                amount: accountPayable.groupingDetails?.amount ?? 0,
            };
        }
        else if (isBillTypeDependent && accountPayable.type) {
            const billTypeDependent = {
                [BillType.PIX]: {
                    image: pixSent,
                    title: t('accounts-payable.operation-type-pix'),
                    amount: accountPayable?.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [BillType.ACCOUNT]: {
                    image: pixSent,
                    title: t('accounts-payable.operation-type-pix'),
                    amount: accountPayable?.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [BillType.BARCODE]: {
                    image: billetPaid,
                    title: t('accounts-payable.operation-type-billet'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
            };
            return billTypeDependent[accountPayable.type];
        }
        else {
            const data = {
                [OperationCode.BANKSLIP]: {
                    image: billetPaid,
                    title: t('accounts-payable.operation-type-billet'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [OperationCode.PAYROLL]: {
                    image: userGroup,
                    title: t('accounts-payable.operation-type-payroll'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [OperationCode.EMPLOYEE_VACATION]: {
                    image: vacation,
                    title: t('accounts-payable.operation-type-vacation'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [OperationCode.EMPLOYEE_TERMINATION_FEES]: {
                    image: userGroup,
                    title: t('accounts-payable.operation-type-termination-fees'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [OperationCode.EMPLOYEE_13TH_SALARY]: {
                    image: userGroup,
                    title: t('accounts-payable.operation-type-13th-salary'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [OperationCode.INVOICE]: {
                    image: invoice,
                    title: t('accounts-payable.operation-type-invoice'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [OperationCode.UTILITIES]: {
                    image: pixSent,
                    title: t('accounts-payable.operation-type-utilities'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [OperationCode.TAX]: {
                    image: pixSent,
                    title: t('accounts-payable.operation-type-tax'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [OperationCode.TR_FEES]: {
                    image: pixSent,
                    title: t('accounts-payable.operation-type-tr-fees'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
                [OperationCode.EMPLOYEE_OTHER_PAYMENTS]: {
                    image: userGroup,
                    title: t('accounts-payable.operation-type-other-payments'),
                    amount: accountPayable.amount ?? 0,
                    description: accountPayable?.title ?? '',
                },
            };
            return data[accountPayable.operationCode];
        }
    }
    function toExtract(accountsPayables, isWhiteListed) {
        const accountsPayableList = [];
        [...accountsPayables]
            .sort((a, b) => {
            const [yearA, monthA, dayA] = a.dueDate.split('-').map(Number);
            const [yearB, monthB, dayB] = b.dueDate.split('-').map(Number);
            const dateA = new Date(yearA, monthA - 1, dayA).getTime();
            const dateB = new Date(yearB, monthB - 1, dayB).getTime();
            return dateA - dateB;
        })
            .forEach(accountsPayable => {
            const dateIndex = getDateIndex(formatLongDayAndMonthOrToday(`${accountsPayable.dueDate}T12:00:00.000Z`), accountsPayableList);
            const { image, title, amount, description } = getData(accountsPayable, isWhiteListed);
            accountsPayableList[dateIndex].data.push({
                ...accountsPayable,
                amount: formatCurrency(amount)?.toString(),
                title: title,
                image: image,
                description: description,
            });
        });
        return accountsPayableList;
    }
    return { toExtract, setTotalAmountAccountsPayable, totalAmountAccountsPayable };
}
